import { z } from 'zod';
import { paginationSchema } from './api.js';
import { INDIAN_STATES, INDIAN_STATE_NAMES, INVOICE_STATUSES, PAYMENT_MODES, TAX_RATES } from './enums.js';
/**
 * Invoicing — GBV-DEV-2026-001 Section 05.
 *
 * A tax invoice is a legal document, so the schema is stricter than most:
 * amounts are integer paise (never Float, never a rupee string), the tax rate
 * must be a real GST slab, and place of supply must be a state we recognise —
 * it is what decides CGST+SGST versus IGST.
 */
/** Amounts arrive in paise so nothing is ever parsed out of a decimal string. */
const paise = z
    .number({ invalid_type_error: 'Enter an amount.' })
    .int('Amounts are in whole paise.')
    .min(0, 'An amount cannot be negative.')
    // ₹10 crore a line. High enough for any SME, low enough that a stray
    // paise/rupee mix-up fails loudly instead of writing nonsense to the ledger.
    .max(100_00_00_000, 'That amount is larger than this system accepts.');
const stateName = z
    .string()
    .trim()
    .min(2, 'Choose a state.')
    .refine((v) => INDIAN_STATE_NAMES.some((s) => s.toLowerCase() === v.toLowerCase()), 'Use a state or union territory name as written on the GST portal.');
export const invoiceLineSchema = z.object({
    description: z.string().trim().min(2, 'Describe what is being billed.').max(300),
    /** HSN for goods, SAC for services. Optional below ₹5 crore turnover. */
    hsnSac: z
        .string()
        .trim()
        .regex(/^[0-9]{4,8}$/, 'An HSN or SAC code is 4 to 8 digits.')
        .optional()
        .or(z.literal('')),
    quantity: z.number().int('Quantity must be a whole number.').min(1).max(100_000),
    unitPricePaise: paise,
    taxRate: z.union([z.literal(0), z.literal(5), z.literal(12), z.literal(18), z.literal(28)], {
        errorMap: () => ({ message: `GST rate must be one of ${TAX_RATES.join(', ')}%.` }),
    }),
});
/** The four documents this editor produces, and how each one is numbered. */
export const SALES_DOC_TYPES = ['QUOTE', 'PROPOSAL', 'PROFORMA', 'TAX_INVOICE'];
export const SALES_DOC_LABELS = {
    QUOTE: {
        label: 'Quote',
        code: 'QT',
        hint: 'A price, offered. No tax is charged and nothing is owed.',
    },
    PROPOSAL: {
        label: 'Proposal',
        code: 'PR',
        hint: 'Scope and price together, for work still being decided.',
    },
    PROFORMA: {
        label: 'Proforma invoice',
        code: 'PI',
        hint: 'A request for payment before the work. Not a tax invoice, and no input credit against it.',
    },
    TAX_INVOICE: {
        label: 'Tax invoice',
        code: 'IV',
        hint: 'The GST document. Once sent it belongs to the return, so it can be voided but never edited.',
    },
};
export const DOC_APPROVALS = ['NOT_REQUIRED', 'PENDING', 'APPROVED', 'REJECTED'];
export const invoiceCreateSchema = z
    .object({
    docType: z.enum(SALES_DOC_TYPES).default('TAX_INVOICE'),
    /** Either, or neither for a walk-in typed by hand. */
    customerCompanyId: z.string().cuid().nullable().optional(),
    customerPersonId: z.string().cuid().nullable().optional(),
    buyerName: z.string().trim().min(2, 'Enter who this invoice is for.').max(160),
    buyerGstin: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/, 'That is not a valid 15-character GSTIN.')
        .optional()
        .or(z.literal('')),
    buyerState: stateName,
    /**
     * Usually the buyer's state, but not always — a service delivered
     * elsewhere is supplied elsewhere. Kept separate so the difference is a
     * deliberate choice rather than an accident of copying one field.
     */
    placeOfSupply: stateName,
    issueDate: z.coerce.date(),
    dueDate: z.coerce.date(),
    notes: z.string().trim().max(1000).optional().or(z.literal('')),
    lineItems: z
        .array(invoiceLineSchema)
        .min(1, 'An invoice needs at least one line.')
        .max(50, 'Split this across more than one invoice.'),
})
    .refine((v) => v.dueDate >= v.issueDate, {
    path: ['dueDate'],
    message: 'The due date cannot fall before the issue date.',
})
    .refine((v) => !v.buyerGstin || v.buyerGstin.slice(0, 2) === gstStateCode(v.buyerState), {
    path: ['buyerGstin'],
    // Catches the common paste error: last client's GSTIN against this
    // client's address. The first two digits of a GSTIN *are* the state.
    message: 'This GSTIN belongs to a different state than the one selected.',
});
/** Drafts only. A sent invoice is immutable — void it and raise a new one. */
export const invoiceUpdateSchema = invoiceCreateSchema;
export const paymentRecordSchema = z.object({
    amountPaise: paise.refine((v) => v > 0, 'A payment has to be more than zero.'),
    mode: z.enum(PAYMENT_MODES),
    reference: z.string().trim().max(120).optional().or(z.literal('')),
    receivedAt: z.coerce.date(),
});
export const invoiceVoidSchema = z.object({
    reason: z
        .string()
        .trim()
        .min(5, 'Say why — a voided tax invoice needs a reason on the record.')
        .max(400),
});
export const invoiceListQuery = paginationSchema.extend({
    status: z.enum(INVOICE_STATUSES).optional(),
    search: z.string().trim().max(120).optional(),
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});
/** The two-digit GST code for a state name, matched case-insensitively. */
function gstStateCode(name) {
    const wanted = name.trim().toLowerCase();
    return INDIAN_STATES.find((s) => s.name.toLowerCase() === wanted)?.code;
}
//# sourceMappingURL=invoice.schema.js.map